import React, { useCallback, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useForm } from '@travelity/form';
import {
    Language,
    subscriptionKeys,
    User,
    userDetailsToUserDetailsDto,
    useRegister,
    UserOccupation,
    useUpdateUser,
    RegisterUsersReqDto,
} from '@travelity/api';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks';

import { getLastCompletedStep } from './register-steps.utils';
import { PersonalInfo } from './components/personal-info';
import { OccupationStep } from './steps/occupation-step';
import { FullNameStep } from './steps/full-name-step';
import { PhoneNumber } from './steps/phoneNumber';
import { LanguageStep } from './steps/language-step';
import { CurrencyStep } from './steps/currency-step';
import { TimeZoneStep } from './steps/timezone-step';
import { WorkweekStep } from './steps/workweek-step';
import { SubscriptionStep } from './steps/subscription-step';

export interface RegisterStepsProps {
    user?: User;
}

export const RegisterSteps: React.FC<RegisterStepsProps> = props => {
    const { user } = props;
    const { showError } = useToast();
    const { user: auth0User } = useAuth0();
    const [currentStep, setCurrentStep] = React.useState(
        getLastCompletedStep(user) + 1
    );

    const { mutate: create } = useRegister({
        onSuccess: () => {
            setCurrentStep(currentStep + 1);
        },
        onError: () => {
            showError('REGISTER_FAILURE');
        },
    });
    const { mutate: update } = useUpdateUser({
        onSuccess: () => {
            setCurrentStep(currentStep + 1);
        },
        onError: () => {
            showError('UPDATE_USER_FAILURE');
        },
    });

    const navigate = useNavigate();

    const onBack = useCallback(() => {
        setCurrentStep(prev => (prev === 8 ? prev - 1 : prev - 1));
    }, []);

    const { Form, getValues, setValue, watch, submit } = useForm({
        onSubmit: useCallback(
            (data: Partial<User>) => {
                if (currentStep === 8 && auth0User?.email) {
                    create({
                        ...(userDetailsToUserDetailsDto({
                            ...data,
                        }) as RegisterUsersReqDto),
                        subscription_key: subscriptionKeys[
                            data.subscriptionName as string
                        ] as string,
                    });
                } else if (currentStep === 9) {
                    update(
                        userDetailsToUserDetailsDto({
                            ...data,
                            subscriptionName: 'free',
                        })
                    );
                }
            },
            [currentStep, auth0User]
        ),
        defaultValues: user || { subscriptionName: 'free' },
        mode: 'onChange',
        validateInitially: true,
    });

    useEffect(() => {
        if (auth0User) {
            const userForm = getValues();
            if (!userForm.emails?.length) {
                setValue('emails', [{ value: auth0User.email as string }]);
            }
        }
    }, [auth0User]);

    return (
        <Box
            sx={{
                px: { xs: currentStep > 6 ? 1.5 : 4, sm: 0 },
                bgcolor: '#fff',
                mt: 2,
                height: 'calc(100% - 16px)',
                '> form': { height: 1 },
            }}
        >
            <PerfectScrollbar>
                {currentStep === 1 && (
                    <OccupationStep
                        occupation={watch('occupation')}
                        next={occupation => {
                            setValue(
                                'occupation',
                                occupation as UserOccupation
                            );
                            setCurrentStep(currentStep + 1);
                        }}
                    />
                )}
                {currentStep === 2 && (
                    <FullNameStep
                        fullName={watch('fullName')}
                        back={onBack}
                        next={fullName => {
                            setValue('fullName', fullName);
                            setCurrentStep(currentStep + 1);
                        }}
                    />
                )}
                {currentStep === 3 && (
                    <PhoneNumber
                        numbers={watch('numbers')}
                        back={onBack}
                        next={numbers => {
                            setValue('numbers', numbers);
                            setCurrentStep(currentStep + 1);
                        }}
                    />
                )}
                {currentStep === 4 && (
                    <LanguageStep
                        languages={watch('languages')}
                        back={onBack}
                        next={l => {
                            setValue('languages', l as Language[]);
                            setCurrentStep(currentStep + 1);
                        }}
                    />
                )}
                {currentStep === 5 && (
                    <CurrencyStep
                        currency={watch('currency')}
                        back={onBack}
                        next={c => {
                            setValue('currency', c);
                            setCurrentStep(currentStep + 1);
                        }}
                    />
                )}
                {currentStep === 6 && (
                    <TimeZoneStep
                        timeZone={watch('timeZone')}
                        back={onBack}
                        next={t => {
                            setValue('timeZone', t);
                            setCurrentStep(currentStep + 1);
                        }}
                    />
                )}
                {currentStep === 7 && (
                    <SubscriptionStep
                        back={onBack}
                        next={s => {
                            setValue('subscriptionName', s);
                            setCurrentStep(currentStep + 1);
                        }}
                    />
                )}
                {currentStep === 8 && (
                    <WorkweekStep
                        subscription={watch('subscription') as string}
                        weekdays={watch('weekdays')}
                        weekdayHours={watch('weekdayHours')}
                        // back={onBack}
                        next={(workdays, weekdayHours) => {
                            setValue('weekdays', workdays);
                            setValue('weekdayHours', weekdayHours);
                            submit();
                        }}
                    />
                )}
                {currentStep > 8 && (
                    <Form>
                        <Stack
                            sx={{
                                py: 5,
                                height: 1,
                                minHeight: 'calc(100vh - 84px)',
                            }}
                            gap={2}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Typography
                                sx={{
                                    color: '#C0C4CE',
                                    fontSize: '24px',
                                }}
                            >
                                Personal Information
                            </Typography>
                            <PersonalInfo
                                onSkip={() => {
                                    navigate(0);
                                }}
                            />
                        </Stack>
                    </Form>
                )}
            </PerfectScrollbar>
        </Box>
    );
};
